import { useState } from 'react'
import { Button, Card, Form, Input, Typography, App as AntdApp } from 'antd'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'

import apiClient from '../../api/client'

// Two pages share this file:
//   /reset-request   — enter email, queue reset mail
//   /reset           — accept token (?token=...) + new password

export function PasswordResetRequest() {
  const { t } = useTranslation()
  const _ = AntdApp.useApp() // keep provider ctx accessible (no toast here)
  void _
  const [submitted, setSubmitted] = useState(false)

  const request = useMutation({
    mutationFn: async (values: { email: string }) => {
      await apiClient.post('/api/v1/portal/auth/password-reset/request', values)
    },
    onSuccess: () => {
      setSubmitted(true)
    },
    onError: () => {
      // The backend always returns 200 to prevent enumeration; an
      // error here means transport. Still show the success view to
      // not leak.
      setSubmitted(true)
    },
  })

  return (
    <div className="portal-login-wrapper">
      <Card className="portal-login-card">
        <Typography.Title level={3} className="portal-login-title">
          {t('portalPasswordReset.requestTitle', 'Passwort zurücksetzen')}
        </Typography.Title>
        {submitted ? (
          <>
            <Typography.Paragraph>
              {t('portalPasswordReset.requested',
                'Wir haben Ihre Anfrage erhalten. Falls die E-Mail-Adresse einem Konto zugeordnet ist, erhalten Sie in Kürze eine Mail mit einem Reset-Link.')}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" className="text-sm">
              {t('portalPasswordReset.checkSpam',
                'Bitte prüfen Sie auch den Spam-Ordner. Der Link ist 1 Stunde gültig.')}
            </Typography.Paragraph>
            <Link to="/portal/login">
              <Button block>
                {t('portalPasswordReset.backToLogin', 'Zurück zum Login')}
              </Button>
            </Link>
          </>
        ) : (
          <>
            <Typography.Paragraph type="secondary">
              {t('portalPasswordReset.requestIntro',
                'Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Reset-Link.')}
            </Typography.Paragraph>
            <Form layout="vertical"
              onFinish={(v) => request.mutate(v as { email: string })}>
              <Form.Item label={t('portalPasswordReset.email', 'E-Mail')}
                name="email" rules={[{ required: true, type: 'email' }]}>
                <Input autoFocus autoComplete="email" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block
                loading={request.isPending}>
                {t('portalPasswordReset.requestSubmit', 'Reset-Link anfordern')}
              </Button>
            </Form>
            <Typography.Paragraph className="text-center mt-8 text-sm">
              <Link to="/portal/login">
                {t('portalPasswordReset.backToLogin', 'Zurück zum Login')}
              </Link>
            </Typography.Paragraph>
          </>
        )}
      </Card>
    </div>
  )
}

export function PasswordResetConfirm() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const confirm = useMutation({
    mutationFn: async (values: { new_password: string }) => {
      await apiClient.post('/api/v1/portal/auth/password-reset/confirm', {
        token, new_password: values.new_password,
      })
    },
    onSuccess: () => {
      message.success(t('portalPasswordReset.success', 'Passwort gesetzt — bitte einloggen.'))
      navigate('/portal/login')
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error
        || t('portalPasswordReset.failed', 'Token ungültig oder abgelaufen'))
    },
  })

  if (!token) {
    return (
      <div className="portal-login-wrapper">
        <Card className="portal-login-card">
          <Typography.Title level={3} className="portal-login-title">
            {t('portalPasswordReset.confirmTitle', 'Passwort setzen')}
          </Typography.Title>
          <Typography.Paragraph type="danger">
            {t('portalPasswordReset.missingToken',
              'Kein Token in der URL. Bitte öffnen Sie den Link aus der Mail.')}
          </Typography.Paragraph>
          <Link to="/portal/reset-request">
            <Button block>
              {t('portalPasswordReset.requestAgain', 'Neuen Reset-Link anfordern')}
            </Button>
          </Link>
        </Card>
      </div>
    )
  }

  return (
    <div className="portal-login-wrapper">
      <Card className="portal-login-card">
        <Typography.Title level={3} className="portal-login-title">
          {t('portalPasswordReset.confirmTitle', 'Passwort setzen')}
        </Typography.Title>
        <Form layout="vertical"
          onFinish={(v) => confirm.mutate(v as { new_password: string })}>
          <Form.Item name="new_password" label={t('portalPasswordReset.newPwd', 'Neues Passwort')}
            rules={[{ required: true, min: 12,
              message: t('portalPasswordReset.pwdRule', 'Mindestens 12 Zeichen.') }]}>
            <Input.Password autoComplete="new-password" autoFocus />
          </Form.Item>
          <Form.Item dependencies={['new_password']} label={t('portalPasswordReset.confirmPwd', 'Bestätigen')}
            name="confirm" rules={[{ required: true }, ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || value === getFieldValue('new_password')) return Promise.resolve()
                return Promise.reject(new Error(
                  t('portalPasswordReset.pwdMismatch', 'Passwörter stimmen nicht überein.')))
              },
            })]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block
            loading={confirm.isPending}>
            {t('portalPasswordReset.submit', 'Passwort setzen')}
          </Button>
        </Form>
      </Card>
    </div>
  )
}
